<template>
  <div class="q-px-lg">
    <div class="row q-col-gutter-xl">
      <div class="col-12 q-mt-xl">
        <div class="flex items-center justify-between">
          <div class="text-md text-weight-medium text-neutral">
            Associated
            {{
              isAgent
                ? "Customers & Vendors"
                : useChangeCase(linkUserRole ?? "", "capitalCase").value
            }}
          </div>
          <PBtn
            :label="`Link ${linkUserRole ?? 'Party'}`"
            flat
            dense
            :disable="isCustomersVendorsNotActive"
            @click="linkUser"
          />
        </div>

        <q-separator spaced />
      </div>
    </div>
    <PTable
      ref="pTable"
      v-model:selected="selected"
      :pagination="{ rowsPerPage: 100 }"
      :columns="columns"
      :rows="associatedUsers"
      :row-key="'_id'"
      class="text-xs"
      table-header-class="text-neutral text-weight-medium"
      flat
      hide-bottom
      wrap-cells
      :loading="isLoading"
      binary-state-sort
    >
      <template #body-cell-name="cellProps">
        <q-td :props="cellProps">
          <div class="col">
            <PPopover :value="cellProps.value.shop_name" :max-length="12" />
            <span class="flex no-wrap text-xxs text-neutral-80">
              {{
                cellProps.value.gstin ? cellProps.value.gstin : "Unregistered"
              }}
            </span>
          </div>
        </q-td>
      </template>

      <template #body-cell-contact="cellProps">
        <q-td :props="cellProps">
          <div class="flex items-center no-wrap q-mb-xs">
            <q-avatar
              color="neutral-50"
              font-size="28px"
              size="44px"
              text-color="neutral-10"
            >
              <span class="text-uppercase">
                {{ getUserAvatarInitials(cellProps.value.name) }}
              </span>
            </q-avatar>
            <div class="column q-ml-xs">
              <span class="text-capitalize">{{ cellProps.value.name }}</span>
              <span class="flex no-wrap text-xxs text-neutral-80 q-mt-xs">{{
                cellProps.value.phone
              }}</span>
              <span class="flex no-wrap text-xxs text-neutral-80">{{
                cellProps.value.email
              }}</span>
            </div>
          </div>
        </q-td>
      </template>

      <template #body-cell-role="cellProps">
        <q-td :props="cellProps">
          <PBadge
            :label="cellProps.value.role === 'seller' ? 'Vendor' : 'Customer'"
            :type="cellProps.value.role === 'seller' ? 'Success' : 'Warning'"
          />
        </q-td>
      </template>

      <template #body-cell-status="cellProps">
        <q-td :props="cellProps">
          <PBadge
            v-if="cellProps.value.is_active !== '-'"
            :label="cellProps.value.is_active ? 'Active' : 'Deactivated'"
            :type="cellProps.value.is_active ? 'Active' : 'Deactivated'"
          />
          <span v-else>{{ cellProps.value.is_active }}</span>
        </q-td>
      </template>

      <template #body-cell-stage="cellProps">
        <q-td :props="cellProps">
          <PBadge
            v-if="cellProps.value.application_status"
            :label="cellProps.value.application_status"
            :type="cellProps.value.application_status"
            class="text-capitalize q-my-xs"
          />
          <span v-else>-</span>
        </q-td>
      </template>

      <template #body-cell-action="cellProps">
        <q-td :props="cellProps">
          <div class="flex no-wrap">
            <PBtn
              :loading="isUnlinking[cellProps.row.id]"
              dense
              flat
              rounded
              @click="() => unlinkUser(cellProps.row)"
            >
              <UnlinkIcon class="text-error-70 icon--md" />
              <PTooltip> Unlink </PTooltip>
            </PBtn>
            <PBtn
              v-if="!props.isAgent"
              :loading="isFetchingDealSetting[cellProps.rowIndex]"
              dense
              flat
              rounded
            >
              <SettingIcon
                class="text-primary icon--md"
                @click="
                  () => openDealSettings(cellProps.row, cellProps.rowIndex)
                "
              />
              <PTooltip> Deal Setting </PTooltip>
            </PBtn>
            <PBtn
              :loading="buttonLoader[cellProps.row.id]"
              dense
              flat
              rounded
              @click="editUser(cellProps.row)"
            >
              <ViewIcon class="icon--md" />
              <PTooltip> View Details </PTooltip>
            </PBtn>
          </div>
        </q-td>
      </template>
    </PTable>
  </div>
</template>
<script setup>
import { useQuasar } from "quasar";

import UnlinkIcon from "assets/icons/unlink.svg";
import SettingIcon from "assets/icons/setting.svg";
import ViewIcon from "assets/icons/view-detail.svg";
import LinkUserDialogForm from "./LinkUserDialogForm.vue";
import PConfirmDialog from "src/components/PConfirmDialog.vue";

import { useChangeCase } from "@vueuse/integrations/useChangeCase";

import { isEmpty } from "src/assets/scripts/utilities/helpers";

const $q = useQuasar();

const { associatedUsers, user } = storeToRefs(useUserStore());

const {
  getAssociatedUser,
  getUser,
  setAssociatedUser,
  putUnlinkAssociatedUsers,
} = useUserStore();

const selected = ref([]);
const isLoading = ref(false);
const isUnlinking = reactive({});
const buttonLoader = reactive({});

const props = defineProps({
  isAgent: {
    type: Boolean,
    default: false,
  },
});

const isCustomersVendorsNotActive = computed(() => {
  return !unref(user)?.is_active;
});

const linkUserRole = computed({
  get() {
    let role = unref(user)?.basicInfo?.role;

    if (role === "seller") return "customer";

    if (role === "buyer") return "vendor";

    return null;
  },
});

const linkUser = () => {
  $q.dialog({
    component: LinkUserDialogForm,
    componentProps: {
      isAgent: props.isAgent,
      linkUserRole: linkUserRole.value,
    },
  });
};

const unlinkUser = async (linkedUser) => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      text: `Are you sure you want to unlink ${linkedUser.shop_name} from ${props.isAgent ? unref(user)?.name : unref(user)?.businessDetails?.shop_name}?`,
      isFooterVisible: true,
      submittable: true,
      cancellable: true,
      submitLabel: "Unlink",
      cancelLabel: "Cancel",
      closable: true,
      onConfirm: async () => {
        try {
          isUnlinking[linkedUser.id] = true;

          await putUnlinkAssociatedUsers({
            unlink_user: unref(linkedUser).id,
            user_role: unref(user).role,
            isAgent: props.isAgent,
          });

          isUnlinking[linkedUser.id] = false;
        } catch (error) {
          console.error(error);
        } finally {
          isUnlinking[linkedUser.id] = false;
        }
      },
    },
  });
};

const columns = computed(() => {
  return [
    { align: "left", name: "name", label: "Business", field: (row) => row },
    { align: "left", name: "contact", label: "Contact", field: (row) => row },
    {
      align: "center",
      name: "role",
      label: "Type",
      field: (row) => row,
      hidden: !props.isAgent,
      sort: (a, b) => {
        const rank = (v) => (v === "seller" ? 1 : 0);

        return rank(a.role) - rank(b.role);
      },
      sortable: true,
    },
    { align: "center", name: "status", label: "Status", field: (row) => row },
    {
      align: "center",
      name: "stage",
      label: "Stage",
      field: (row) => row,
      hidden: props.isAgent,
    },
    {
      align: "left",
      classes: "sticky--right",
      headerClasses: "sticky--right",
      name: "action",
      label: "",
    },
  ];
});

const getUserAvatarInitials = (name) => {
  const nameComponents = name?.split(" ");
  const firstInitial = nameComponents?.[0]?.charAt(0)?.toUpperCase() || "U";

  return firstInitial;
};

const editUser = async (selectedUser) => {
  try {
    if (unref(user)._id !== selectedUser.id) {
      buttonLoader[selectedUser.id] = true;

      setAssociatedUser(unref(user)._id);

      await getUser(selectedUser.id);

      buttonLoader[selectedUser.id] = false;
    }
  } catch (error) {
    console.error(error);

    buttonLoader[selectedUser.id] = false;
  }
};

const { dealSetting } = storeToRefs(useDealSettingStore());
const { toggleDealSettingForm, getDealSetting } = useDealSettingStore();

const isFetchingDealSetting = ref({});

const fetchDealSetting = async ({ buyer, rowIndex, seller }) => {
  try {
    const buyer_id = buyer._id || buyer.id;
    const seller_id = seller._id || seller.id;

    unref(isFetchingDealSetting)[rowIndex] = true;

    await getDealSetting({ buyer_id, seller_id });
  } catch (error) {
    console.error(error);
  } finally {
    unref(isFetchingDealSetting)[rowIndex] = false;
  }
};

const openDealSettings = async (associatedUser, rowIndex) => {
  try {
    const buyer = unref(user).role === "buyer" ? unref(user) : associatedUser;
    const seller = unref(user).role === "seller" ? unref(user) : associatedUser;

    await fetchDealSetting({ buyer, rowIndex, seller });

    toggleDealSettingForm({
      level: 0,
      open: true,
      edit: !isEmpty(unref(dealSetting)),
      props: { buyer, seller },
    });
  } catch (error) {
    console.error(error);
  }
};

onBeforeMount(async () => {
  isLoading.value = true;

  try {
    await getAssociatedUser({ isAgent: props.isAgent });
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
});
</script>
